import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  UploadCloud, ChevronRight, ChevronLeft, FileText,
  CheckCircle, AlertTriangle, Edit2, Trash2, Save, X, Loader, Check,
  Camera, Database, Eye, RotateCcw, RotateCw, BarChart3, Settings,
  Search, Filter, Download, Trash, ChevronDown, ChevronUp, Sparkles,
  LogOut, History, Users, User, Shield, FolderOpen, ScanLine
} from 'lucide-react';
import AISettings from './components/AISettings';
import LoginPage from './components/LoginPage';
import AdminDashboard from './components/AdminDashboard';
import ProcessHistory from './components/ProcessHistory';
import FileManager from './components/FileManager';
import ScannerCapture from './components/ScannerCapture';
import ResultView from './components/ResultView';
import LandingPage from './components/LandingPage';
import { useAuth } from './context/AuthContext';

// --- API Helper Functions ---
async function extractDataWithGemini(base64Data, mimeType, authToken) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 180000);

  try {
    const response = await fetch('/api/extract', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
      body: JSON.stringify({ base64Data, mimeType }),
      signal: controller.signal
    });

    clearTimeout(timeout);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Extraction failed on the server.');
    }
    return await response.json();
  } catch (err) {
    clearTimeout(timeout);
    if (err.name === 'AbortError') {
      throw new Error('Request timed out after 3 minutes. Try a smaller/compressed image.');
    }
    throw err;
  }
}

async function saveToDatabase(records, authToken, processId) {
  const response = await fetch('/api/save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
    body: JSON.stringify({ records, processId })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to save to database");
  }
  return await response.json();
}

async function fetchDatabaseRecords(authToken) {
  const response = await fetch('/api/records', { headers: { Authorization: `Bearer ${authToken}` } });
  if (!response.ok) throw new Error('Failed to fetch records');
  return await response.json();
}

async function deleteDatabaseRecord(id, authToken) {
  const response = await fetch(`/api/records/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } });
  if (!response.ok) throw new Error('Failed to delete record');
  return await response.json();
}

async function updateDatabaseRecord(id, data, authToken) {
  const response = await fetch(`/api/records/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Failed to update record');
  return await response.json();
}

async function fetchStats(authToken) {
  const response = await fetch('/api/stats', { headers: { Authorization: `Bearer ${authToken}` } });
  if (!response.ok) throw new Error('Failed to fetch stats');
  return await response.json();
}

// --- Components ---

function Stepper({ step, approvedCount }) {
  const steps = [
    { label: 'Upload', icon: UploadCloud },
    { label: 'Preview', icon: Eye },
    { label: 'Process', icon: Loader },
    { label: 'Review', icon: CheckCircle },
    { label: 'Database', icon: Database }
  ];

  return (
    <div className="flex items-center justify-center mb-8 px-4">
      {steps.map((s, i) => {
        const Icon = s.icon;
        const isCompleted = step > i + 1;
        const isActive = step === i + 1;

        return (
          <React.Fragment key={s.label}>
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                isCompleted ? 'bg-green-500 text-white' :
                isActive ? 'bg-blue-600 text-white ring-4 ring-blue-200 shadow-lg' :
                'bg-slate-200 text-slate-500'
              }`}>
                {isCompleted ? <Check size={20} /> : <Icon size={18} />}
              </div>
              <span className={`text-xs mt-2 font-medium ${isActive ? 'text-blue-700' : 'text-slate-500'}`}>
                {s.label}
              </span>
            </div>
            {i < 4 && (
              <div className={`flex-1 h-1 mx-2 sm:mx-4 rounded transition-all ${
                isCompleted ? 'bg-green-500' : 'bg-slate-200'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Detect OS and return scanner folder hint
function getScannerFolderHint() {
  const ua = navigator.userAgent;
  const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
  if (isMobile) return { os: 'mobile', hint: null };
  if (/Win/i.test(ua))  return { os: 'windows', hint: 'Windows: Pictures\\Scanned Documents  or  Documents\\Scanned' };
  if (/Mac/i.test(ua))  return { os: 'mac',     hint: 'macOS: ~/Pictures  or  ~/Documents/Scanned' };
  if (/Linux/i.test(ua)) return { os: 'linux',  hint: 'Linux: ~/Pictures  or  ~/Documents' };
  return { os: 'unknown', hint: null };
}

// Use File System Access API when available (Chrome/Edge) for startIn:'pictures',
// otherwise fall back to hidden <input type="file">
async function pickFilesFromScanner(fileInputRef, onFilesSelected) {
  if (typeof window.showOpenFilePicker === 'function') {
    try {
      const handles = await window.showOpenFilePicker({
        multiple: true,
        startIn: 'pictures',
        types: [{
          description: 'Images',
          accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.tiff', '.bmp'] }
        }]
      });
      const files = await Promise.all(handles.map(h => h.getFile()));
      if (files.length > 0) onFilesSelected(files);
      return;
    } catch (err) {
      if (err.name === 'AbortError') return; // user cancelled — don't fall through
    }
  }
  // Fallback: regular input
  fileInputRef.current?.click();
}

function UploadSection({ onFilesSelected, onScannerOpen }) {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const { hint } = getScannerFolderHint();

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) onFilesSelected(files);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) onFilesSelected(files);
  };

  return (
    <div className="max-w-2xl mx-auto mt-12">
      <div
        className={`border-3 border-dashed rounded-2xl p-16 flex flex-col items-center justify-center bg-white transition-all cursor-pointer shadow-sm ${
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-slate-300 hover:bg-blue-50 hover:border-blue-400'
        }`}
        onClick={() => pickFilesFromScanner(fileInputRef, onFilesSelected)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="bg-blue-100 p-4 rounded-full mb-6">
          <UploadCloud size={48} className="text-blue-600" />
        </div>
        <h2 className="text-2xl font-semibold mb-2 text-slate-800">Upload Marks Slips</h2>
        <p className="text-slate-500 mb-4 text-center max-w-md">
          Drag and drop your images here, or click to browse.<br />
          Supports JPG, PNG (Single or Multiple)
        </p>
        {hint && (
          <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-5 max-w-sm text-center">
            <FolderOpen size={13} className="shrink-0 text-slate-400" />
            <span>Scanner saves to: <span className="font-mono text-slate-500">{hint.split(':')[1]?.trim()}</span></span>
          </div>
        )}
        <div className="flex gap-3 flex-wrap justify-center">
          <button className="btn-primary" onClick={e => { e.stopPropagation(); pickFilesFromScanner(fileInputRef, onFilesSelected); }}>
            <UploadCloud size={20} />
            Select Files
          </button>
          <button
            onClick={e => { e.stopPropagation(); onScannerOpen?.(); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl shadow-sm transition-colors">
            <ScanLine size={20} />
            Scan / Camera
          </button>
        </div>
        <input
          type="file"
          multiple
          accept="image/*,.pdf"
          className="hidden"
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        <p className="text-xs text-slate-400 mt-4">
          Upload files or capture directly from your camera or scanner
        </p>
      </div>
    </div>
  );
}

function PreviewSection({ files, previews, currentIndex, onNext, onPrev, onProcess, onCancel, onJumpTo, onRotate, onDelete }) {
  return (
    <div className="card p-6 flex flex-col lg:flex-row gap-8">
      <div className="flex-1 flex flex-col items-center justify-center bg-slate-100 rounded-xl overflow-hidden relative min-h-[400px]">
        <img
          src={previews[currentIndex].dataUrl}
          alt={`Preview ${currentIndex + 1}`}
          className="max-h-[600px] w-auto object-contain"
        />
        {/* Rotation controls — top-left overlay */}
        <div className="absolute top-4 left-4 flex gap-2">
          <button
            onClick={() => onRotate(currentIndex, -90)}
            title="Rotate 90° counter-clockwise"
            className="bg-slate-800/80 hover:bg-slate-900 text-white p-2 rounded-full backdrop-blur-sm transition-all"
          >
            <RotateCcw size={18} />
          </button>
          <button
            onClick={() => onRotate(currentIndex, 90)}
            title="Rotate 90° clockwise"
            className="bg-slate-800/80 hover:bg-slate-900 text-white p-2 rounded-full backdrop-blur-sm transition-all"
          >
            <RotateCw size={18} />
          </button>
        </div>
        {/* Delete button — top-right, separate from filename badge */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <div className="bg-slate-800/80 text-white px-3 py-1 rounded-lg text-sm backdrop-blur-sm max-w-[160px] truncate">
            {files[currentIndex].name}
          </div>
          <button
            onClick={() => onDelete(currentIndex)}
            title="Remove this file"
            className="bg-red-600/85 hover:bg-red-700 text-white p-1.5 rounded-full backdrop-blur-sm transition-all"
          >
            <Trash2 size={15} />
          </button>
        </div>
        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
          <button
            onClick={onPrev}
            disabled={currentIndex === 0}
            className="bg-slate-800/80 hover:bg-slate-900 text-white p-2 rounded-full disabled:opacity-50 transition-all backdrop-blur-sm"
          >
            <ChevronLeft size={24} />
          </button>
          <div className="bg-slate-800/80 text-white px-4 py-2 rounded-full font-medium text-sm backdrop-blur-sm">
            {currentIndex + 1} of {files.length}
          </div>
          <button
            onClick={onNext}
            disabled={currentIndex === files.length - 1}
            className="bg-slate-800/80 hover:bg-slate-900 text-white p-2 rounded-full disabled:opacity-50 transition-all backdrop-blur-sm"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      </div>

      <div className="w-full lg:w-80 flex flex-col justify-center">
        <h2 className="text-2xl font-bold mb-4 text-slate-800">Ready to Process</h2>

        {/* Queue indicator */}
        {files.length > 1 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-3">
            <div className="flex flex-col items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-sm shrink-0">
              {currentIndex + 1}<span className="text-xs font-normal">/{files.length}</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-blue-800">Sequential extraction</p>
              <p className="text-xs text-blue-600">Each file is processed one at a time. Review &amp; resolve, then proceed to the next.</p>
            </div>
          </div>
        )}

        <div className="bg-slate-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-slate-600 mb-2">
            <span className="font-medium">File:</span> {files[currentIndex].name}
          </p>
          <p className="text-sm text-slate-600 mb-2">
            <span className="font-medium">Size:</span> {(files[currentIndex].size / 1024).toFixed(1)} KB
          </p>
          <p className="text-sm text-slate-600">
            <span className="font-medium">Type:</span> {files[currentIndex].type}
          </p>
        </div>
        <p className="text-slate-600 mb-6">
          The AI will extract data from both tabs and flag any mismatches for your review.
        </p>
        <button onClick={onProcess} className="btn-primary mb-3">
          {files.length > 1 ? `Extract File ${currentIndex + 1} of ${files.length}` : 'Start Extraction'}
          <ChevronRight size={20} />
        </button>
        <button
          onClick={() => onDelete(currentIndex)}
          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 mb-3 border border-red-300 text-red-600 hover:bg-red-50 rounded-xl font-medium text-sm transition-colors"
        >
          <Trash2 size={16} />
          Remove This File
        </button>
        <button onClick={onCancel} className="btn-secondary w-full">
          <RotateCcw size={18} />
          Cancel & Upload New
        </button>

        {files.length > 1 && (
          <div className="mt-6 pt-6 border-t border-slate-200">
            <p className="text-sm font-medium text-slate-600 mb-3">Queue</p>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto scrollbar-thin">
              {files.map((f, i) => (
                <div key={i} className="relative group">
                  <button
                    onClick={() => onJumpTo(i)}
                    title={f.name}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                      i === currentIndex
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {i + 1}
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); onDelete(i); }}
                    title="Remove"
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full items-center justify-center hidden group-hover:flex"
                  >
                    <X size={9} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProcessingSection({ isProcessing, error, onRetry, onCancel }) {
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <div className="bg-red-50 text-red-700 p-8 rounded-2xl max-w-md text-center border border-red-200">
          <AlertTriangle size={48} className="mx-auto mb-4 text-red-500" />
          <h3 className="font-bold text-xl mb-2">Processing Failed</h3>
          <p className="mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button onClick={onCancel} className="btn-secondary">
              <ChevronLeft size={18} />
              Go Back
            </button>
            <button onClick={onRetry} className="btn-danger">
              <RotateCcw size={18} />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-32">
      <div className="relative w-24 h-24 mb-8">
        <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
        <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
        <div className="absolute inset-0 flex items-center justify-center text-blue-600">
          <Loader size={32} className="animate-pulse" />
        </div>
      </div>
      <h2 className="text-2xl font-bold mb-2 text-slate-800">Analyzing Document...</h2>
      <p className="text-slate-500 max-w-md text-center">
        Using AI to extract table rows and detect mismatches. This may take up to 2 minutes.
      </p>
      <div className="mt-8 flex gap-2">
        <span className="badge badge-info">OCR Processing</span>
        <span className="badge badge-info">Data Extraction</span>
        <span className="badge badge-info">Mismatch Detection</span>
      </div>
      <button onClick={onCancel} className="mt-8 btn-secondary text-sm">
        <X size={16} />Cancel
      </button>
    </div>
  );
}

// ─── Number-to-words converter (0–999, handles NEB mark ranges) ───────────────
const ONES = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
              'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen',
              'Seventeen','Eighteen','Nineteen'];
const TENS = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];

function numberToWords(n) {
  const num = parseInt(n, 10);
  if (isNaN(num) || num < 0 || num > 999) return null;
  if (num === 0) return 'Zero';
  let result = '';
  if (num >= 100) {
    result += ONES[Math.floor(num / 100)] + ' Hundred';
    if (num % 100 !== 0) result += ' ';
  }
  const rem = num % 100;
  if (rem > 0) {
    if (rem < 20) {
      result += ONES[rem];
    } else {
      result += TENS[Math.floor(rem / 10)];
      if (rem % 10 !== 0) result += ' ' + ONES[rem % 10];
    }
  }
  return result.trim();
}

// Returns corrected words for a fig, or null if fig isn't a clean integer
function correctedWords(fig) {
  if (!fig) return null;
  const clean = String(fig).trim();
  if (!/^\d+$/.test(clean)) return null;
  return numberToWords(clean);
}
// ──────────────────────────────────────────────────────────────────────────────

// OCR number correction: maps common misread characters to digits
function fixOcrNumber(val) {
  if (!val) return val;
  return String(val)
    .replace(/[Oo]/g, '0')
    .replace(/[Il|]/g, '1')
    .replace(/[Zz]/g, '2')
    .replace(/[Ss]/g, '5')
    .replace(/[Gg]/g, '6')
    .replace(/[Bb]/g, '8')
    .replace(/[^0-9]/g, '');
}

// Normalize words string for comparison: lowercase + collapse whitespace
function normalizeWords(w) {
  return (w || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

// Returns per-token diff between two word strings (case-insensitive)
// Result: array of { token, different } for the 'other' side vs 'base'
function wordDiff(base, other) {
  if (!other) return [];
  const baseTokens = normalizeWords(base).split(' ');
  const otherTokens = other.trim().split(/\s+/);
  return otherTokens.map((tok, i) => ({
    token: tok,
    different: (tok.toLowerCase() !== (baseTokens[i] || '').toLowerCase())
  }));
}

// Shows AI-extracted words with per-word diff highlighting vs the other tab
function WordsDisplay({ fig, words, otherWords, showDiff }) {
  const suggested = correctedWords(fig);
  const aiWords = words || '';
  const needsCorrection = suggested && normalizeWords(aiWords) !== normalizeWords(suggested);

  const tokens = showDiff ? wordDiff(otherWords, aiWords) : null;
  const hasRealDiff = showDiff && tokens?.some(t => t.different);

  return (
    <div className="flex-1 flex flex-col gap-0.5 min-w-0">
      <div className={`italic text-sm leading-tight ${needsCorrection ? 'text-slate-500 line-through decoration-amber-400' : 'text-slate-700'}`}>
        {tokens ? (
          tokens.map((t, i) => (
            <span key={i}>
              {i > 0 ? ' ' : ''}
              <span className={t.different ? 'bg-red-100 text-red-700 rounded px-0.5 font-semibold' : ''}>{t.token}</span>
            </span>
          ))
        ) : (aiWords || '-')}
        {!aiWords && '-'}
      </div>
      {hasRealDiff && (
        <div className="text-xs text-red-500 leading-tight">⚠ word differs</div>
      )}
      {needsCorrection && (
        <div className="text-emerald-700 font-medium text-sm leading-tight flex items-center gap-1">
          <span className="text-amber-500 text-xs">✓</span>
          {suggested}
        </div>
      )}
    </div>
  );
}

// Inline editable fig cell — click to edit, Enter/blur to confirm
function InlineFigEdit({ value, onChange, highlight }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.select(); }, [editing]);

  const commit = () => {
    const fixed = fixOcrNumber(draft) || draft.trim();
    setEditing(false);
    if (fixed !== value) onChange(fixed);
  };

  if (editing) {
    return (
      <div className="relative flex items-center">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setEditing(false); setDraft(value || ''); } }}
          className="w-14 font-bold text-base text-center border-2 border-blue-400 rounded px-1 py-0.5 focus:outline-none focus:border-blue-600"
          maxLength={6}
        />
        {fixOcrNumber(draft) !== draft.trim() && fixOcrNumber(draft) && (
          <span className="absolute -bottom-5 left-0 text-xs text-amber-600 whitespace-nowrap">
            → {fixOcrNumber(draft)}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={() => setEditing(true)}
      title="Click to edit"
      className={`w-12 font-bold text-lg text-center rounded cursor-pointer select-none transition-colors
        ${highlight ? 'text-red-600 bg-red-100 hover:bg-red-200' : 'hover:bg-blue-50 hover:text-blue-700'}`}
    >
      {value || '-'}
    </div>
  );
}

function ReviewSection({
  extractedData,
  resolvedRowIds,
  onApprove,
  onEdit,
  onReject,
  onNextFile,
  onApproveAllMatched,
  onPrevious,
  currentFileIndex,
  totalFiles,
  approvedCount,
  onUpdateExtractedFig
}) {
  const totalRows = extractedData?.rows?.length || 0;
  const resolvedCount = resolvedRowIds.size;
  const progress = totalRows > 0 ? (resolvedCount / totalRows) * 100 : 0;
  // Compute mismatch count with case-insensitive word matching (consistent with UI)
  const mismatchCount = extractedData?.rows?.filter(r => {
    if (resolvedRowIds.has(r.id)) return false;
    const figMismatch = r.tab1?.fig !== r.tab2?.fig;
    const wordsMismatch = normalizeWords(r.tab1?.words) !== normalizeWords(r.tab2?.words);
    const rollMismatch = r.tab1?.roll !== r.tab2?.roll;
    return figMismatch || wordsMismatch || rollMismatch;
  }).length || 0;

  return (
    <div className="space-y-6">
      {/* Header Info */}
      <div className="card p-4 flex flex-wrap gap-x-6 gap-y-2 items-center">
        {[
          { label: 'Year',       value: extractedData.header?.year },
          { label: 'Subject',    value: extractedData.header?.subject },
          { label: 'Centre',     value: extractedData.header?.centre },
          { label: 'Code',       value: extractedData.header?.computer_code },
          { label: 'Class',      value: extractedData.header?.class },
          { label: 'Paper',      value: extractedData.header?.paper },
          { label: 'Page',       value: extractedData.header?.page },
          { label: 'Full Marks', value: extractedData.header?.full_marks },
          { label: 'Pass Marks', value: extractedData.header?.pass_marks },
          { label: 'Packet No',  value: extractedData.header?.packet_no },
        ].filter(f => f.value).map(f => (
          <div key={f.label}>
            <span className="text-slate-500 text-xs uppercase tracking-wider block">{f.label}</span>
            <span className="font-semibold text-slate-800 text-sm">{f.value}</span>
          </div>
        ))}
        <div className="ml-auto flex gap-3">
          {mismatchCount > 0 && (
            <div className="badge badge-error flex items-center gap-1 px-3 py-1.5">
              <AlertTriangle size={14} />
              {mismatchCount} Mismatch{mismatchCount !== 1 ? 'es' : ''}
            </div>
          )}
          <div className="badge badge-warning flex items-center gap-1 px-3 py-1.5">
            <Edit2 size={14} />
            Review Mode
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-lg p-4 border border-slate-200">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-slate-600">Review Progress</span>
          <span className="font-medium text-slate-800">{resolvedCount} of {totalRows} rows resolved</span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Data Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="table-header">
                <th className="p-4 w-16 text-center">ID</th>
                <th className="p-4 border-l border-slate-200 bg-blue-50/50">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                    TAB 1 Extracted
                  </div>
                </th>
                <th className="p-4 border-l border-slate-200 bg-purple-50/50">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                    TAB 2 Extracted
                  </div>
                </th>
                <th className="p-4 border-l border-slate-200 text-center w-64">Resolution</th>
              </tr>
            </thead>
            <tbody>
              {extractedData.rows.map((row) => {
                const isResolved = resolvedRowIds.has(row.id);
                const figMismatch = row.tab1?.fig !== row.tab2?.fig;
                const wordsMismatch = normalizeWords(row.tab1?.words) !== normalizeWords(row.tab2?.words);
                const rollMismatch = row.tab1?.roll !== row.tab2?.roll;
                const isMismatch = figMismatch || wordsMismatch || rollMismatch;

                return (
                  <tr
                    key={row.id}
                    className={`border-b border-slate-100 transition-all ${
                      isResolved
                        ? 'bg-slate-50 opacity-60'
                        : isMismatch
                          ? 'bg-red-50/40 hover:bg-red-50/80'
                          : 'hover:bg-slate-50'
                    }`}
                  >
                    <td className="p-4 text-center font-medium text-slate-500">{row.id}</td>

                    <td className="p-4 border-l border-slate-200">
                      <div className="flex gap-4 items-center">
                        <div className={`w-24 font-mono text-sm bg-white border px-2 py-1 rounded text-center ${rollMismatch ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}>
                          {row.tab1?.roll || '-'}
                        </div>
                        <InlineFigEdit
                          value={row.tab1?.fig}
                          highlight={false}
                          onChange={v => onUpdateExtractedFig && onUpdateExtractedFig(row.id, 'tab1', v)}
                        />
                        <WordsDisplay fig={row.tab1?.fig} words={row.tab1?.words} otherWords={row.tab2?.words} showDiff={wordsMismatch} />
                      </div>
                    </td>

                    <td className="p-4 border-l border-slate-200">
                      <div className="flex gap-4 items-center">
                        <div className={`w-24 font-mono text-sm bg-white border px-2 py-1 rounded text-center ${rollMismatch ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}>
                          {row.tab2?.roll || '-'}
                        </div>
                        <InlineFigEdit
                          value={row.tab2?.fig}
                          highlight={figMismatch}
                          onChange={v => onUpdateExtractedFig && onUpdateExtractedFig(row.id, 'tab2', v)}
                        />
                        <WordsDisplay fig={row.tab2?.fig} words={row.tab2?.words} otherWords={row.tab1?.words} showDiff={wordsMismatch} />
                      </div>
                    </td>

                    <td className="p-4 border-l border-slate-200 text-center">
                      {isResolved ? (
                        <div className="flex items-center justify-center gap-2 text-green-600 font-medium">
                          <CheckCircle size={20} />
                          Resolved
                        </div>
                      ) : isMismatch ? (
                        <div className="flex flex-col gap-2">
                          <div className="flex gap-2">
                            <button
                              onClick={() => onApprove(row, 'tab1')}
                              className="flex-1 bg-blue-100 hover:bg-blue-200 text-blue-800 py-1.5 rounded text-sm font-medium transition-colors"
                            >
                              Use Tab 1
                            </button>
                            <button
                              onClick={() => onApprove(row, 'tab2')}
                              className="flex-1 bg-purple-100 hover:bg-purple-200 text-purple-800 py-1.5 rounded text-sm font-medium transition-colors"
                            >
                              Use Tab 2
                            </button>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => onEdit(row)}
                              className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 py-1.5 rounded text-sm font-medium transition-colors flex items-center justify-center gap-1"
                            >
                              <Edit2 size={14} />
                              Manual Edit
                            </button>
                            <button
                              onClick={() => onReject(row.id)}
                              className="px-2 text-red-500 hover:bg-red-100 rounded transition-colors"
                              title="Reject / Ignore Row"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => onApprove(row, 'tab1')}
                          className="w-full bg-green-500 hover:bg-green-600 text-white py-2 rounded shadow-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          <Check size={18} />
                          Approve
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="card p-4 flex flex-wrap justify-between items-center gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={onPrevious} className="btn-secondary">
            <ChevronLeft size={18} />
            Previous
          </button>
          <div className="text-slate-600 font-medium text-sm">
            {resolvedCount} of {totalRows} rows resolved
            {approvedCount > 0 && (
              <span className="ml-3 text-green-600">
                <CheckCircle size={14} className="inline mr-1" />
                {approvedCount} approved
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {onApproveAllMatched && (() => {
            // Use case-insensitive matching logic consistent with UI display
            const isRowMatched = (r) => {
              const figMatch = r.tab1?.fig === r.tab2?.fig;
              const wordsMatch = normalizeWords(r.tab1?.words) === normalizeWords(r.tab2?.words);
              const rollMatch = r.tab1?.roll === r.tab2?.roll;
              return figMatch && wordsMatch && rollMatch;
            };
            const matchedCount = extractedData?.rows?.filter(r => !resolvedRowIds.has(r.id) && isRowMatched(r)).length || 0;
            return matchedCount > 0 ? (
              <button
                onClick={onApproveAllMatched}
                className="btn-secondary text-green-700 border-green-300 bg-green-50 hover:bg-green-100"
              >
                <CheckCircle size={18} />
                Approve All Matched ({matchedCount})
              </button>
            ) : null;
          })()}
          <button
            onClick={onNextFile}
            disabled={resolvedCount < totalRows}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {currentFileIndex < totalFiles - 1 ? (
              <>
                Next File
                <ChevronRight size={20} />
              </>
            ) : (
              <>
                Finish & View Database
                <Database size={20} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function DatabaseSection({
  approvedRecords,
  onDelete,
  onSaveToDB,
  isSaving,
  onUploadMore,
  onPrevious,
  onEditRecord
}) {
  const { token } = useAuth();
  const [dbRecords, setDbRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInDB, setShowInDB] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (showInDB) {
      loadDatabaseRecords();
      loadStats();
    }
  }, [showInDB]);

  const loadDatabaseRecords = async () => {
    setIsLoading(true);
    try {
      const records = await fetchDatabaseRecords(token);
      setDbRecords(records);
    } catch (error) {
      alert('Error loading records: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const s = await fetchStats(token);
      setStats(s);
    } catch (error) {
      console.error('Stats error:', error);
    }
  };

  const handleDeleteFromDB = async (id) => {
    if (!confirm('Are you sure you want to delete this record from the database?')) return;
    try {
      await deleteDatabaseRecord(id, token);
      setDbRecords(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      alert('Error deleting: ' + error.message);
    }
  };

  const allRecords = [...approvedRecords, ...(showInDB ? dbRecords : [])];
  const filteredRecords = allRecords.filter(r =>
    r.roll?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.roll_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Approved Records</h2>
          <p className="text-slate-500">
            {approvedRecords.length} pending • {dbRecords.length} in database
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search records..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10 w-64"
            />
          </div>
          <button
            onClick={() => setShowInDB(!showInDB)}
            className={`btn-secondary ${showInDB ? 'bg-blue-100 text-blue-700' : ''}`}
          >
            <Database size={18} />
            {showInDB ? 'Hide DB Records' : 'Show DB Records'}
          </button>
          <button onClick={onPrevious} className="btn-secondary">
            <ChevronLeft size={18} />
            Previous
          </button>
          <button onClick={onUploadMore} className="btn-secondary">
            <UploadCloud size={18} />
            Upload More
          </button>
          <button
            onClick={onSaveToDB}
            disabled={isSaving || approvedRecords.length === 0}
            className="btn-primary disabled:opacity-50"
          >
            {isSaving ? (
              <Loader size={18} className="animate-spin" />
            ) : (
              <Save size={18} />
            )}
            {isSaving ? 'Saving...' : 'Push to DB'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card p-4 flex items-center gap-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Database size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-slate-500 text-sm">Total Records</p>
              <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-4">
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircle size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-slate-500 text-sm">Added Today</p>
              <p className="text-2xl font-bold text-slate-800">{stats.today}</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-4">
            <div className="bg-purple-100 p-3 rounded-lg">
              <BarChart3 size={24} className="text-purple-600" />
            </div>
            <div>
              <p className="text-slate-500 text-sm">Top Subject</p>
              <p className="text-lg font-bold text-slate-800 truncate">
                {stats.topSubjects?.[0]?.subject || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Records Table */}
      {filteredRecords.length === 0 ? (
        <div className="card p-16 text-center">
          <Database size={48} className="mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500 text-lg">No records found.</p>
          <p className="text-slate-400">Upload and process files to see records here.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-left">
            <thead className="table-header">
              <tr>
                <th className="p-4">Year/Subject</th>
                <th className="p-4">Centre</th>
                <th className="p-4">Roll Number</th>
                <th className="p-4">Mark (Figure)</th>
                <th className="p-4">Mark (Words)</th>
                <th className="p-4 text-center">Status</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredRecords.map((record) => {
                const isPending = approvedRecords.some(r => r.id === record.id);
                const displayRecord = record.roll ? record : {
                  ...record,
                  roll: record.roll_number,
                  fig: record.marks_in_figure,
                  words: record.marks_in_words
                };

                return (
                  <tr key={record.id} className="hover:bg-slate-50">
                    <td className="p-4">
                      <div className="text-slate-800 font-medium">{displayRecord.header?.subject || record.subject}</div>
                      <div className="text-slate-500 text-sm">{displayRecord.header?.year || record.year}</div>
                    </td>
                    <td className="p-4 text-slate-600">{displayRecord.header?.centre || record.centre}</td>
                    <td className="p-4 font-mono text-slate-800">{displayRecord.roll || record.roll_number}</td>
                    <td className="p-4 font-bold text-slate-800">{displayRecord.fig || record.marks_in_figure}</td>
                    <td className="p-4 text-slate-600 italic">{displayRecord.words || record.marks_in_words}</td>
                    <td className="p-4 text-center">
                      {isPending ? (
                        <span className="badge badge-warning">Pending Save</span>
                      ) : (
                        <span className="badge badge-success">Saved</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex justify-center gap-2">
                        {isPending ? (
                          <button
                            onClick={() => onDelete(record.id)}
                            className="text-red-500 hover:bg-red-50 p-2 rounded transition-colors"
                            title="Remove from pending"
                          >
                            <Trash2 size={18} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDeleteFromDB(record.id)}
                            className="text-red-500 hover:bg-red-50 p-2 rounded transition-colors"
                            title="Delete from database"
                          >
                            <Trash size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EditModal({ row, isOpen, onClose, onSave }) {
  const [form, setForm] = useState({ roll: '', fig: '', words: '' });

  useEffect(() => {
    if (row && isOpen) {
      setForm({
        roll: row.tab1?.roll || '',
        fig: row.tab1?.fig || '',
        words: row.tab1?.words || ''
      });
    }
  }, [row, isOpen]);

  if (!isOpen || !row) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
        <div className="bg-slate-50 border-b border-slate-200 p-4 flex justify-between items-center">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Edit2 size={18} />
            Manual Correction
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm">
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="font-semibold text-amber-800 text-xs mb-1 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  TAB 1
                </div>
                <div className="text-amber-700">Roll: {row.tab1?.roll}</div>
                <div className="text-amber-700">Fig: <strong>{row.tab1?.fig}</strong></div>
                <div className="text-amber-700">Words: {row.tab1?.words}</div>
              </div>
              <div className="w-px bg-amber-200"></div>
              <div className="flex-1">
                <div className="font-semibold text-amber-800 text-xs mb-1 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                  TAB 2
                </div>
                <div className="text-amber-700">Roll: {row.tab2?.roll}</div>
                <div className="text-amber-700">Fig: <strong>{row.tab2?.fig}</strong></div>
                <div className="text-amber-700">Words: {row.tab2?.words}</div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Roll Number</label>
            <input
              type="text"
              value={form.roll}
              onChange={(e) => setForm({ ...form, roll: e.target.value })}
              className="input-field font-mono"
            />
          </div>

          <div className="flex gap-4">
            <div className="w-1/3">
              <label className="block text-sm font-medium text-slate-700 mb-1">Marks (Fig)</label>
              <input
                type="text"
                value={form.fig}
                onChange={(e) => setForm({ ...form, fig: e.target.value })}
                className="input-field font-bold"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">Marks (Words)</label>
              <input
                type="text"
                value={form.words}
                onChange={(e) => setForm({ ...form, words: e.target.value })}
                className="input-field"
              />
            </div>
          </div>
        </div>

        <div className="bg-slate-50 border-t border-slate-200 p-4 flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => onSave(row, form)}
            className="btn-primary"
          >
            <Save size={18} />
            Save & Approve
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main App Component ---
export default function App() {
  const { user, token, logout, authFetch, loading: authLoading, isAdmin } = useAuth();

  // Navigation state: 'digitize' | 'history' | 'admin'
  const [activePage, setActivePage] = useState(
    user?.user_type?.toLowerCase() === 'admin' ? 'admin' : 'digitize'
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Switch to admin page when admin user logs in
  useEffect(() => {
    if (user?.user_type?.toLowerCase() === 'admin') {
      setActivePage('admin');
    }
  }, [user]);

  // AI Settings Modal State
  const [showAISettings, setShowAISettings] = useState(false);
  const [activeAIProvider, setActiveAIProvider] = useState(null);

  // Workflow State
  const [step, setStep] = useState(1);

  // Process tracking
  const [processId] = useState(() => crypto.randomUUID());

  // Load active provider on mount
  useEffect(() => {
    if (!token) return;
    authFetch('/api/ai/active')
      .then(res => res.ok ? res.json() : null)
      .then(data => data && setActiveAIProvider(data))
      .catch(() => null);
  }, [token]);

  // Scanner modal
  const [showScanner, setShowScanner] = useState(false);

  // Rotate a preview image by deg degrees (+90 CW, -90 CCW), update previews state
  const handleRotate = (index, deg) => {
    const preview = previews[index];
    if (!preview) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const swap = deg % 180 !== 0;
      canvas.width  = swap ? img.height : img.width;
      canvas.height = swap ? img.width  : img.height;
      const ctx = canvas.getContext('2d');
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((deg * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      const newDataUrl = canvas.toDataURL(preview.mimeType || 'image/jpeg', 0.95);
      const newBase64  = newDataUrl.split(',')[1];
      setPreviews(prev => prev.map((p, i) =>
        i === index ? { ...p, dataUrl: newDataUrl, base64: newBase64 } : p
      ));
    };
    img.src = preview.dataUrl;
  };

  // File State
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [fileServerIds, setFileServerIds] = useState([]); // IDs from uploaded_files table
  const pendingUploadRef = useRef(null); // Promise for ongoing background upload

  const handleDeletePreviewFile = (index) => {
    const newFiles    = files.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    if (newFiles.length === 0) {
      setFiles([]);
      setPreviews([]);
      setStep(1);
      return;
    }
    setFiles(newFiles);
    setPreviews(newPreviews);
    // Keep current index in bounds
    setCurrentFileIndex(prev => (prev >= newFiles.length ? newFiles.length - 1 : prev === index && index > 0 ? index - 1 : prev < index ? prev : prev - 1));
  };

  // Processing State
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState(null);

  // Data State
  const [extractedData, setExtractedData] = useState(null);
  const [approvedRecords, setApprovedRecords] = useState([]);
  const [resolvedRowIds, setResolvedRowIds] = useState(new Set());

  // Edit Modal State
  const [editingRow, setEditingRow] = useState(null);

  // Save State
  const [isSaving, setIsSaving] = useState(false);

  // File Handlers
  const handleFileUpload = useCallback((selectedFiles) => {
    const validFiles = selectedFiles.filter(f => f.type.startsWith('image/'));

    if (validFiles.length === 0) {
      alert('Please select valid image files (JPG, PNG)');
      return;
    }

    setFiles(validFiles);

    const fileReaders = validFiles.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve({
          file,
          dataUrl: reader.result,
          base64: reader.result.split(',')[1],
          mimeType: file.type
        });
        reader.readAsDataURL(file);
      });
    });

    Promise.all(fileReaders).then(results => {
      setPreviews(results);
      setStep(2);
      setCurrentFileIndex(0);
      setExtractedData(null);
      setResolvedRowIds(new Set());

      // Upload files to server storage in background, store IDs back on each file
      if (token) {
        const formData = new FormData();
        validFiles.forEach(f => formData.append('files', f));
        pendingUploadRef.current = fetch('/api/files/upload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }).then(r => r.json()).then(data => {
          if (data.files) {
            const ids = data.files.map(f => f.id);
            setFileServerIds(ids);
            // Attach server ID to each file object so startProcessing can update status
            setFiles(prev => prev.map((f, i) => Object.assign(f, { uploadedFileId: ids[i] || null })));
            return ids;
          }
          return [];
        }).catch(() => []).finally(() => { pendingUploadRef.current = null; });
      }
    });
  }, [token]);

  const handleNextPreview = () => {
    if (currentFileIndex < files.length - 1) {
      setCurrentFileIndex(prev => prev + 1);
      setExtractedData(null);
    }
  };

  const handlePrevPreview = () => {
    if (currentFileIndex > 0) {
      setCurrentFileIndex(prev => prev - 1);
      setExtractedData(null);
    }
  };

  const handleJumpToFile = (index) => {
    setCurrentFileIndex(index);
    setExtractedData(null);
  };

  // Approve all matched rows at once (case-insensitive word matching)
  const approveAllMatched = () => {
    if (!extractedData?.rows) return;
    const isRowMatched = (r) => {
      const figMatch = r.tab1?.fig === r.tab2?.fig;
      const wordsMatch = normalizeWords(r.tab1?.words) === normalizeWords(r.tab2?.words);
      const rollMatch = r.tab1?.roll === r.tab2?.roll;
      return figMatch && wordsMatch && rollMatch;
    };
    const unresolved = extractedData.rows.filter(r => !resolvedRowIds.has(r.id) && isRowMatched(r));
    unresolved.forEach(row => {
      const finalData = { ...row.tab1 };
      const fixedWords = correctedWords(finalData.fig);
      if (fixedWords) finalData.words = fixedWords;
      const newRecord = {
        ...finalData,
        sourceImageIndex: currentFileIndex,
        header: extractedData.header,
        originalId: row.id,
        id: crypto.randomUUID()
      };
      setApprovedRecords(prev => [...prev, newRecord]);
      setResolvedRowIds(prev => new Set(prev).add(row.id));
    });
  };

  // Processing Handler
  const startProcessing = async () => {
    setStep(3);
    setIsProcessing(true);
    setProcessingError(null);
    setResolvedRowIds(new Set());

    const currentImg = previews[currentFileIndex];
    const currentFile = files[currentFileIndex];

    // Wait for any pending background upload to complete so we have the file ID
    if (pendingUploadRef.current) {
      try { await pendingUploadRef.current; } catch {}
    }
    // Re-read uploadedFileId after potential upload completion
    const freshFile = files[currentFileIndex];
    const uploadedFileId = freshFile?.uploadedFileId || fileServerIds[currentFileIndex] || null;

    // Log to process history (with file reference if available)
    try {
      await authFetch('/api/history', {
        method: 'POST',
        body: JSON.stringify({
          id: processId,
          filename: currentFile?.name,
          file_size: currentFile?.size,
          mime_type: currentImg.mimeType,
          status: 'processing',
          uploaded_file_id: uploadedFileId
        })
      });
    } catch {}

    // Mark file as processing
    if (uploadedFileId) {
      try { await authFetch(`/api/files/${uploadedFileId}`, { method: 'PATCH', body: JSON.stringify({ status: 'processing' }) }); } catch {}
    }

    try {
      const data = await extractDataWithGemini(currentImg.base64, currentImg.mimeType, token);
      setExtractedData(data);
      setStep(4);
      // Mark file as processed
      if (uploadedFileId) {
        try { await authFetch(`/api/files/${uploadedFileId}`, { method: 'PATCH', body: JSON.stringify({ status: 'processed', records_extracted: data.rows?.length || 0 }) }); } catch {}
      }
      // Update history with extracted count
      try {
        await authFetch(`/api/history/${processId}`, {
          method: 'PUT',
          body: JSON.stringify({
            records_extracted: data.rows?.length || 0,
            status: 'reviewed',
            year: data.header?.year,
            subject: data.header?.subject,
            centre: data.header?.centre
          })
        });
      } catch {}
    } catch (err) {
      console.error(err);
      setProcessingError(err.message || 'Failed to extract data.');
      try { await authFetch(`/api/history/${processId}`, { method: 'PUT', body: JSON.stringify({ status: 'cancelled' }) }); } catch {}
      if (uploadedFileId) {
        try { await authFetch(`/api/files/${uploadedFileId}`, { method: 'PATCH', body: JSON.stringify({ status: 'failed' }) }); } catch {}
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Update a fig value directly in extractedData (inline edit in review table)
  const handleUpdateExtractedFig = (rowId, tab, newFig) => {
    setExtractedData(prev => {
      if (!prev?.rows) return prev;
      const rows = prev.rows.map(r => {
        if (r.id !== rowId) return r;
        const updated = { ...r, [tab]: { ...r[tab], fig: newFig } };
        // Re-evaluate mismatch after edit (words comparison is case-insensitive)
        updated.isMismatch =
          updated.tab1?.fig !== updated.tab2?.fig ||
          normalizeWords(updated.tab1?.words) !== normalizeWords(updated.tab2?.words) ||
          updated.tab1?.roll !== updated.tab2?.roll;
        return updated;
      });
      return { ...prev, rows };
    });
  };

  // Review Handlers
  const approveRow = (row, source, manualData = null) => {
    let finalData = {};
    if (source === 'tab1') finalData = { ...row.tab1 };
    else if (source === 'tab2') finalData = { ...row.tab2 };
    else if (source === 'manual' && manualData) finalData = { ...manualData };

    // Auto-correct words from fig if fig is a clean number
    const fixedWords = correctedWords(finalData.fig);
    if (fixedWords) finalData.words = fixedWords;

    const newRecord = {
      ...finalData,
      sourceImageIndex: currentFileIndex,
      header: extractedData.header,
      originalId: row.id,
      id: crypto.randomUUID()
    };

    setApprovedRecords(prev => [...prev, newRecord]);
    setResolvedRowIds(prev => new Set(prev).add(row.id));
    setEditingRow(null);
  };

  const openEditModal = (row) => setEditingRow(row);

  const rejectRow = (rowId) => {
    setResolvedRowIds(prev => new Set(prev).add(rowId));
  };

  const handleNextFileAfterReview = () => {
    if (currentFileIndex < files.length - 1) {
      const nextIndex = currentFileIndex + 1;
      setCurrentFileIndex(nextIndex);
      setExtractedData(null);
      setResolvedRowIds(new Set());
      // Auto-start extraction for next file immediately (sequential mode)
      setStep(3);
      setIsProcessing(true);
      setProcessingError(null);
      const nextImg  = previews[nextIndex];
      const nextFile = files[nextIndex];
      authFetch('/api/history', {
        method: 'POST',
        body: JSON.stringify({
          id: processId,
          filename: nextFile?.name,
          file_size: nextFile?.size,
          mime_type: nextImg?.mimeType,
          status: 'processing'
        })
      }).catch(() => {});
      extractDataWithGemini(nextImg.base64, nextImg.mimeType, token)
        .then(data => {
          setExtractedData(data);
          setStep(4);
          authFetch(`/api/history/${processId}`, {
            method: 'PUT',
            body: JSON.stringify({
              records_extracted: data.rows?.length || 0,
              status: 'reviewed',
              year: data.header?.year,
              subject: data.header?.subject,
              centre: data.header?.centre
            })
          }).catch(() => {});
        })
        .catch(err => {
          setProcessingError(err.message || 'Failed to extract data.');
          authFetch(`/api/history/${processId}`, { method: 'PUT', body: JSON.stringify({ status: 'cancelled' }) }).catch(() => {});
        })
        .finally(() => setIsProcessing(false));
    } else {
      setStep(5);
    }
  };

  // Database Handlers
  const deleteApprovedRecord = (id) => {
    setApprovedRecords(prev => prev.filter(r => r.id !== id));
  };

  const handleSaveToDatabase = async () => {
    if (approvedRecords.length === 0) {
      alert('No records to save');
      return;
    }

    setIsSaving(true);
    try {
      const result = await saveToDatabase(approvedRecords, token, processId);
      let msg = `Successfully saved ${result.savedCount} records to database!`;
      if (result.skippedCount > 0) {
        msg += `\n\n${result.skippedCount} duplicate(s) skipped (same roll number + subject + year already exists).`;
      }
      alert(msg);
      // Update process history
      try {
        await authFetch(`/api/history/${processId}`, {
          method: 'PUT',
          body: JSON.stringify({
            records_approved: approvedRecords.length,
            records_saved: result.savedCount,
            status: 'saved'
          })
        });
      } catch {}
      // Sync uploaded_files status → 'saved' for each source file
      try {
        const fileIds = [...new Set(files.map(f => f.uploadedFileId).filter(Boolean))];
        for (const fid of fileIds) {
          await authFetch(`/api/files/${fid}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: 'saved', records_saved: result.savedCount })
          });
        }
      } catch {}
      setApprovedRecords([]);
    } catch (error) {
      alert('Error saving data: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUploadMore = () => {
    setStep(1);
    setFiles([]);
    setPreviews([]);
    setCurrentFileIndex(0);
    setExtractedData(null);
    setResolvedRowIds(new Set());
  };

  // Load a file from the Files page into the Digitize workflow
  const handleLoadFileIntoWorkflow = (fileOrResult) => {
    setActivePage('digitize');
    // If it's a reprocess result with extracted data, jump to review
    if (fileOrResult?.data?.rows) {
      setExtractedData(fileOrResult.data);
      setStep(4);
      // Track the source file ID so we can mark it 'saved' after save
      if (fileOrResult.fileId) {
        setFiles(prev => {
          const synced = [...(prev || [])];
          if (!synced[0]) synced[0] = {};
          synced[0] = { ...synced[0], uploadedFileId: fileOrResult.fileId };
          return synced;
        });
      }
    } else {
      // Otherwise reset to step 1 so user can pick a new file
      setStep(1);
    }
  };

  // Show public result lookup page (no login required)
  if (window.location.search.includes('result') || window.location.pathname.startsWith('/result')) {
    return <ResultView />;
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <span className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!user || !token) {
    // Direct login URL (?login) or landing page
    if (window.location.search.includes('login')) {
      return <LoginPage />;
    }
    return (
      <LandingPage
        onLoginClick={() => window.location.search = '?login'}
        onResultClick={() => window.location.search = '?result'}
      />
    );
  }

  // Render
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans">
      {/* Header / Navbar */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 p-1.5 rounded-lg text-white">
                <FileText size={20} />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-base font-bold text-slate-800 leading-tight">Marks Digitizer</h1>
                <p className="text-xs text-slate-400">National Examinations Board</p>
              </div>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {user?.user_type?.toLowerCase() !== 'viewer' && (
                <button onClick={() => setActivePage('digitize')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activePage === 'digitize' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-100'
                  }`}>
                  <Camera size={16} />Digitize
                </button>
              )}
              <button onClick={() => setActivePage('files')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activePage === 'files' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-100'
                }`}>
                <FolderOpen size={16} />Files
              </button>
              <button onClick={() => setActivePage('history')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activePage === 'history' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-100'
                }`}>
                <History size={16} />History
              </button>
              {isAdmin && (<>
                <button onClick={() => setActivePage('admin')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activePage === 'admin' ? 'bg-purple-50 text-purple-700' : 'text-slate-600 hover:bg-slate-100'
                  }`}>
                  <Shield size={16} />Admin
                </button>
                <button onClick={() => setShowAISettings(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">
                  <Settings size={16} />AI Settings
                </button>
              </>)}
            </nav>

            {/* User + Logout */}
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2 text-sm">
                <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-700 font-semibold text-xs">{user.name?.charAt(0).toUpperCase()}</span>
                </div>
                <div className="hidden lg:block text-right">
                  <div className="font-medium text-slate-700 text-xs leading-tight">{user.name}</div>
                  <div className="text-slate-400 text-xs">{user.office_type}</div>
                </div>
              </div>
              {approvedRecords.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">
                  <AlertTriangle size={10} className="inline mr-1" />{approvedRecords.length}
                </div>
              )}
              <button onClick={logout} title="Logout"
                className="flex items-center gap-1.5 px-3 py-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg text-sm transition-colors">
                <LogOut size={16} /><span className="hidden sm:inline">Logout</span>
              </button>

              {/* Mobile menu button */}
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg">
                <Settings size={18} />
              </button>
            </div>
          </div>

          {/* Mobile Nav Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-slate-100 py-2 space-y-1 pb-3">
              {[
                ...(user?.user_type?.toLowerCase() !== 'viewer' ? [['digitize', <Camera size={16} />, 'Digitize']] : []),
                ['files',   <FolderOpen size={16} />, 'Files'],
                ['history', <History size={16} />, 'History'],
                ...(isAdmin ? [['admin', <Shield size={16} />, 'Admin']] : [])
              ].map(([page, icon, label]) => (
                <button key={page} onClick={() => { setActivePage(page); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium ${
                    activePage === page ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-100'
                  }`}>
                  {icon}{label}
                </button>
              ))}
              {isAdmin && (
                <button onClick={() => { setShowAISettings(true); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">
                  <Settings size={16} />AI Settings
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Page Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {activePage === 'history' && <ProcessHistory />}
        {activePage === 'files' && (
          <FileManager
            adminMode={isAdmin}
            onLoadFileIntoWorkflow={handleLoadFileIntoWorkflow}
          />
        )}
        {activePage === 'admin' && isAdmin && <AdminDashboard />}
        {activePage === 'digitize' && (
          <div className="pb-10">
            <Stepper step={step} approvedCount={approvedRecords.length} />

            {step === 1 && (
              <>
                <UploadSection onFilesSelected={handleFileUpload} onScannerOpen={() => setShowScanner(true)} />
                {showScanner && (
                  <ScannerCapture
                    onCapture={(capturedFiles) => { handleFileUpload(capturedFiles); }}
                    onClose={() => setShowScanner(false)}
                  />
                )}
              </>
            )}

            {step === 2 && previews.length > 0 && (
              <PreviewSection
                files={files}
                previews={previews}
                currentIndex={currentFileIndex}
                onNext={handleNextPreview}
                onPrev={handlePrevPreview}
                onProcess={startProcessing}
                onCancel={() => setStep(1)}
                onJumpTo={handleJumpToFile}
                onRotate={handleRotate}
                onDelete={handleDeletePreviewFile}
              />
            )}

            {step === 3 && (
              <ProcessingSection
                isProcessing={isProcessing}
                error={processingError}
                onRetry={startProcessing}
                onCancel={() => { setStep(2); setIsProcessing(false); setProcessingError(null); }}
              />
            )}

            {step === 4 && extractedData && (
              <ReviewSection
                extractedData={extractedData}
                resolvedRowIds={resolvedRowIds}
                onApprove={approveRow}
                onEdit={openEditModal}
                onReject={rejectRow}
                onNextFile={handleNextFileAfterReview}
                onApproveAllMatched={approveAllMatched}
                onPrevious={() => { setStep(2); setExtractedData(null); setResolvedRowIds(new Set()); }}
                currentFileIndex={currentFileIndex}
                totalFiles={files.length}
                approvedCount={approvedRecords.length}
                onUpdateExtractedFig={handleUpdateExtractedFig}
              />
            )}

            {step === 5 && (
              <DatabaseSection
                approvedRecords={approvedRecords}
                onDelete={deleteApprovedRecord}
                onSaveToDB={handleSaveToDatabase}
                isSaving={isSaving}
                onUploadMore={handleUploadMore}
                onPrevious={() => setStep(4)}
              />
            )}
          </div>
        )}
      </main>

      {/* Edit Modal */}
      <EditModal
        row={editingRow}
        isOpen={!!editingRow}
        onClose={() => setEditingRow(null)}
        onSave={(row, form) => approveRow(row, 'manual', form)}
      />

      {/* AI Settings Modal */}
      <AISettings
        isOpen={showAISettings}
        onClose={() => {
          setShowAISettings(false);
          authFetch('/api/ai/active')
            .then(res => res.ok ? res.json() : null)
            .then(data => data && setActiveAIProvider(data))
            .catch(() => null);
        }}
      />
    </div>
  );
}
